import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { isCurrentMatter } from "../utils/matterIdentity";
import {
  categoryUpdate,
  deleteDocumentApi,
  deleteFolderApi,
  fetchCategoryLists,
  fetchMatterDetailFolders,
  fetchMatterDetails,
  fetchMatterDetailsList,
  fetchOpenDocsList,
  getShareContext,
  getShareRecipientsApi,
  postOpenDoc,
  remindReviewApi,
  removeOpenDoc,
  reviewActionApi,
  reviewQueueActionApi,
  shareDocumentApi,
  fetchTemplateFieldsApi,
  mergeTemplateFieldsApi,
  previewTemplateFieldsApi,
  uploadTemplateApi,
  fetchReviewNotesApi,
  submitActionApi,
  postMatterHighlight,
  deleteMatterHighlight,
  sendForESignApi,
  initiateESignApi,
  cancelSignatureRequestApi,
  fetchExternalShareDetail,
  generatePassword,
  externalShare,
  revokeShare,
  getDocumentDetail,
  fetchPreviewPDF,
  remindSignatureApi,
  fetchBreadCrumb,
  updateTabordering,
  chatBotApi,
  postLastPageView,
  moveDocumentApi,
  moveDocumentsBatchApi
} from "../service/matter";
import { fetchBookmarksAPI, getBookmarksAPI } from "../service/matter";
import {
  createFolder,
  renameFolder,
  uploadMatterDetail,
} from "../service/uploadService";


// Helper function to deduplicate and sort openDocs by position
const deduplicateAndSortOpenDocs = (openDocs) => {
  if (!Array.isArray(openDocs)) return [];
  
  // Create a Map to deduplicate by document ID
  const uniqueDocs = new Map();
  for (const doc of openDocs) {
    const docId = doc.document || doc.document_id || doc.id;
    if (docId && !uniqueDocs.has(docId)) {
      uniqueDocs.set(docId, doc);
    }
  };
  
  // Convert back to array and sort by position
  return Array.from(uniqueDocs.values()).sort((a, b) => {
    const posA = a.position || a.order || 0;
    const posB = b.position || b.order || 0;
    return posA - posB;
  });
};

const normalizeDocumentId = (documentId) => {
  const raw = String(documentId || "").trim();
  if (!raw) return "";
  return raw.toUpperCase().startsWith("D") ? `D${raw.substring(1)}` : `D${raw}`;
};

const getSubfolders = (folder) =>
  Array.isArray(folder?.subfolders) ? folder.subfolders : folder?.folders || [];

const documentMatches = (doc, documentUniqueId) => {
  const bareId = documentUniqueId.startsWith("D")
    ? documentUniqueId.substring(1)
    : documentUniqueId;
  return (
    String(doc?.unique_id || "").toUpperCase() ===
      documentUniqueId.toUpperCase() ||
    String(doc?.id) === bareId ||
    String(doc?.document_id) === bareId
  );
};

const makeOptimisticFolder = ({ matterId, parent, name }) => ({
  id: null,
  unique_id: `optimistic-${String(name).toLowerCase().replace(/\s+/g, "-")}`,
  name,
  matter: matterId,
  parent: parent?.id || null,
  relative_path: parent?.relative_path
    ? `${parent.relative_path}/${encodeURIComponent(name)}`
    : "",
  subfolders: [],
  documents: [],
  is_optimistic: true,
});

const findOrCreateFolderByName = ({ folders, matterId, parent, name }) => {
  const targetName = String(name || "").trim().toLowerCase();
  let folder = folders.find(
    (item) => String(item?.name || "").trim().toLowerCase() === targetName
  );
  if (!folder) {
    folder = makeOptimisticFolder({ matterId, parent, name });
    folders.push(folder);
  }
  if (!Array.isArray(folder.subfolders)) folder.subfolders = [];
  if (!Array.isArray(folder.documents)) folder.documents = [];
  return folder;
};

const findOrCreateDestinationFolder = ({
  folders,
  matterId,
  parent,
  name,
  destinationFolder,
}) => {
  const targetId = destinationFolder?.id;
  const targetUniqueId = destinationFolder?.unique_id;
  let folder = folders.find(
    (item) =>
      (targetId != null && String(item?.id) === String(targetId)) ||
      (targetUniqueId && item?.unique_id === targetUniqueId)
  );
  if (!folder) {
    folder = findOrCreateFolderByName({ folders, matterId, parent, name });
  }
  if (destinationFolder) {
    folder.id = destinationFolder.id ?? folder.id;
    folder.unique_id = destinationFolder.unique_id || folder.unique_id;
    folder.name = destinationFolder.name || folder.name;
    folder.relative_path = destinationFolder.relative_path || folder.relative_path;
    folder.parent_id = destinationFolder.parent_id ?? folder.parent_id;
    folder.is_optimistic = false;
  }
  if (!Array.isArray(folder.subfolders)) folder.subfolders = [];
  if (!Array.isArray(folder.documents)) folder.documents = [];
  return folder;
};

const removeDocumentFromTree = (tree, documentUniqueId) => {
  if (!tree || !documentUniqueId) return null;

  const removeFromDocuments = (holder) => {
    if (!Array.isArray(holder?.documents)) return null;
    const index = holder.documents.findIndex((doc) =>
      documentMatches(doc, documentUniqueId)
    );
    if (index === -1) return null;
    const [document] = holder.documents.splice(index, 1);
    return document;
  };

  const removedFromRoot = removeFromDocuments(tree);
  if (removedFromRoot) return removedFromRoot;

  const visitFolders = (folders) => {
    if (!Array.isArray(folders)) return null;
    for (const folder of folders) {
      const removed = removeFromDocuments(folder);
      if (removed) return removed;
      const nested = visitFolders(getSubfolders(folder));
      if (nested) return nested;
    }
    return null;
  };

  return visitFolders(tree.folders || []);
};

const folderMatchesCurrent = (folder, currentFolderId) => {
  if (!currentFolderId || currentFolderId === "root") return false;
  const raw = String(currentFolderId);
  return (
    String(folder?.id) === raw ||
    folder?.unique_id === raw ||
    folder?.unique_id === `F${raw}`
  );
};

const applyOptimisticCategoryMove = (state, payload) => {
  const category = String(payload?.category || "").trim();
  const documentUniqueId = normalizeDocumentId(payload?.documentId);
  if (!category || !documentUniqueId || !state.matterDetailsTree) return;

  const tree = state.matterDetailsTree;
  const removedDocument = removeDocumentFromTree(tree, documentUniqueId);
  const sourceDocument =
    removedDocument ||
    payload?.document ||
    state.updateMeta ||
    state.documentDetail ||
    {};
  if (!sourceDocument || Object.keys(sourceDocument).length === 0) return;

  if (!Array.isArray(tree.folders)) tree.folders = [];
  const aiClaimFile = findOrCreateFolderByName({
    folders: tree.folders,
    matterId: tree.id || sourceDocument.matter,
    parent: null,
    name: "AI Claim File",
  });
  const categoryFolder = findOrCreateDestinationFolder({
    folders: aiClaimFile.subfolders,
    matterId: tree.id || sourceDocument.matter,
    parent: aiClaimFile,
    name: category,
    destinationFolder: payload?.destinationFolder,
  });

  const movedDocument = {
    ...sourceDocument,
    unique_id: documentUniqueId,
    category,
    doc_category: category,
    categorized_by: "manual",
    folder: categoryFolder.id ?? sourceDocument.folder,
    folder_id: categoryFolder.id ?? sourceDocument.folder_id,
    relative_path: categoryFolder.relative_path
      ? `${categoryFolder.relative_path}/${documentUniqueId}`
      : sourceDocument.relative_path,
  };

  const existingIndex = categoryFolder.documents.findIndex((doc) =>
    documentMatches(doc, documentUniqueId)
  );
  if (existingIndex === -1) {
    categoryFolder.documents.push(movedDocument);
  } else {
    categoryFolder.documents[existingIndex] = movedDocument;
  }

  const currentFolderIsTarget = folderMatchesCurrent(
    categoryFolder,
    payload?.currentFolderId
  );
  const listIndex = state.matterDetailsList.findIndex((doc) =>
    documentMatches(doc, documentUniqueId)
  );
  if (currentFolderIsTarget) {
    if (listIndex === -1) {
      state.matterDetailsList.unshift(movedDocument);
    } else {
      state.matterDetailsList[listIndex] = movedDocument;
    }
  } else if (listIndex !== -1) {
    state.matterDetailsList.splice(listIndex, 1);
    state.totalRecords = Math.max(0, (state.totalRecords || 0) - 1);
  }

  if (documentMatches(state.updateMeta, documentUniqueId)) {
    state.updateMeta = { ...state.updateMeta, ...movedDocument };
  }
  if (documentMatches(state.documentDetail, documentUniqueId)) {
    state.documentDetail = { ...state.documentDetail, ...movedDocument };
  }
};

// Id matching for the generic folder-move path. Deliberately NOT built on
// normalizeDocumentId + documentMatches: that pair assumes numeric pks, so
// it strips the first character of any id that happens to start with "d"/"D"
// — and document pks are UUIDs now, where a leading hex "d" is perfectly
// legal. This matcher instead compares the raw value against unique_id
// (with and without the "D" prefix), id, and document_id, without ever
// mutating the id it was given.
const documentMatchesAnyId = (doc, rawId) => {
  const raw = String(rawId ?? "").trim();
  if (!raw || !doc) return false;
  const upper = raw.toUpperCase();
  const uid = String(doc.unique_id || "").toUpperCase();
  return (
    uid === upper ||
    uid === `D${upper}` ||
    String(doc.id) === raw ||
    String(doc.document_id) === raw ||
    (upper.startsWith("D") && String(doc.id).toUpperCase() === upper.slice(1))
  );
};

const documentUniqueIdFor = (rawId, sourceDocument) => {
  if (sourceDocument?.unique_id) return String(sourceDocument.unique_id);
  const raw = String(rawId ?? "").trim();
  return raw.toUpperCase().startsWith("D") ? raw : `D${raw}`;
};

// removeDocumentFromTree, generalized to a predicate (the category-move
// variant above keys on a pre-normalized unique_id).
const removeDocumentFromTreeWhere = (tree, predicate) => {
  if (!tree || typeof predicate !== "function") return null;

  const removeFromDocuments = (holder) => {
    if (!Array.isArray(holder?.documents)) return null;
    const index = holder.documents.findIndex((doc) => predicate(doc));
    if (index === -1) return null;
    const [document] = holder.documents.splice(index, 1);
    return document;
  };

  const removedFromRoot = removeFromDocuments(tree);
  if (removedFromRoot) return removedFromRoot;

  const visitFolders = (folders) => {
    if (!Array.isArray(folders)) return null;
    for (const folder of folders) {
      const removed = removeFromDocuments(folder);
      if (removed) return removed;
      const nested = visitFolders(getSubfolders(folder));
      if (nested) return nested;
    }
    return null;
  };

  return visitFolders(tree.folders || []);
};

// Recursive folder lookup for the generic folder-move path. Accepts a raw
// pk, an F-prefixed unique_id, or anything String()-able; returns the live
// folder object inside the tree (so callers can mutate its documents array
// under Immer) or null when the destination isn't in the loaded tree.
const findFolderInTree = (tree, folderId) => {
  if (folderId == null || folderId === "" || folderId === "root") return null;
  const raw = String(folderId);
  const bare = raw.startsWith("F") ? raw.slice(1) : raw;
  const visit = (folders) => {
    if (!Array.isArray(folders)) return null;
    for (const folder of folders) {
      if (!folder || typeof folder !== "object") continue;
      if (
        String(folder.id) === bare ||
        String(folder.id) === raw ||
        folder.unique_id === raw ||
        folder.unique_id === `F${bare}`
      ) {
        return folder;
      }
      const nested = visit(getSubfolders(folder));
      if (nested) return nested;
    }
    return null;
  };
  return visit(tree?.folders || []);
};

// Generic placement-only move (drag-and-drop / "Move to folder"). Unlike
// applyOptimisticCategoryMove above, this never invents destination folders
// and never touches category fields: the destination must already exist in
// the loaded tree (the user just clicked or dropped on it), and a missing
// destination simply skips the optimistic step — the authoritative
// fetchMatterDetail after the API call reconciles the tree either way.
const applyOptimisticFolderMove = (state, payload) => {
  const rawDocumentId = String(payload?.documentId ?? "").trim();
  if (!rawDocumentId || !state.matterDetailsTree) return;
  const matchesDoc = (doc) => documentMatchesAnyId(doc, rawDocumentId);

  const tree = state.matterDetailsTree;
  const rawDestination = payload?.destinationFolderId;
  const toRoot =
    rawDestination == null || rawDestination === "" || rawDestination === "root";

  let destinationFolder = null;
  if (!toRoot) {
    destinationFolder = findFolderInTree(
      tree,
      payload?.destinationFolder?.id ?? rawDestination
    );
    if (!destinationFolder) return;
    if (!Array.isArray(destinationFolder.documents)) {
      destinationFolder.documents = [];
    }
    if (payload?.destinationFolder) {
      destinationFolder.relative_path =
        payload.destinationFolder.relative_path ||
        destinationFolder.relative_path;
    }
  } else if (!Array.isArray(tree.documents)) {
    tree.documents = [];
  }

  const removedDocument = removeDocumentFromTreeWhere(tree, matchesDoc);
  const sourceDocument =
    removedDocument ||
    payload?.document ||
    {};
  if (!sourceDocument || Object.keys(sourceDocument).length === 0) return;

  const documentUniqueId = documentUniqueIdFor(rawDocumentId, sourceDocument);
  const destinationPath = toRoot
    ? tree.id
      ? `/${tree.id}`
      : ""
    : destinationFolder.relative_path || "";
  const movedDocument = {
    ...sourceDocument,
    ...(payload?.document || {}),
    unique_id: documentUniqueId,
    folder: toRoot ? null : destinationFolder.id ?? sourceDocument.folder,
    folder_id: toRoot ? null : destinationFolder.id ?? sourceDocument.folder_id,
    relative_path: destinationPath
      ? `${destinationPath}/${documentUniqueId}`
      : sourceDocument.relative_path,
  };

  const destinationDocs = toRoot ? tree.documents : destinationFolder.documents;
  const existingIndex = destinationDocs.findIndex(matchesDoc);
  if (existingIndex === -1) {
    destinationDocs.push(movedDocument);
  } else {
    destinationDocs[existingIndex] = movedDocument;
  }

  const currentFolderIsTarget =
    !toRoot &&
    folderMatchesCurrent(destinationFolder, payload?.currentFolderId);
  const listIndex = state.matterDetailsList.findIndex(matchesDoc);
  if (currentFolderIsTarget) {
    if (listIndex === -1) {
      state.matterDetailsList.unshift(movedDocument);
    } else {
      state.matterDetailsList[listIndex] = movedDocument;
    }
  } else if (listIndex !== -1) {
    state.matterDetailsList.splice(listIndex, 1);
    state.totalRecords = Math.max(0, (state.totalRecords || 0) - 1);
  }

  if (matchesDoc(state.updateMeta)) {
    state.updateMeta = { ...state.updateMeta, ...movedDocument };
  }
  if (matchesDoc(state.documentDetail)) {
    state.documentDetail = { ...state.documentDetail, ...movedDocument };
  }
};

export const fetchMatterDetail = createAsyncThunk(
  "matterDetails/fetch",
  async (id, { getState, rejectWithValue }) => {
    try {
      const response = await fetchMatterDetails(id)
      const viewedMatterId = getState()?.websocket?.currentMatterId;
      if (
        viewedMatterId &&
        !isCurrentMatter(viewedMatterId, id, response?.data)
      ) {
        return { stale: true };
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Placement-only document move (drag-and-drop / "Move to folder" menu).
// folderId of null moves the document to the matter root.
export const moveDocumentToFolder = createAsyncThunk(
  "matterDetails/moveDocument",
  async ({ documentId, folderId, onConflict }, { rejectWithValue }) => {
    try {
      const response = await moveDocumentApi({ id: documentId, folderId, onConflict });
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status,
        message:
          error?.response?.data?.detail ||
          "Couldn't move the document — try again.",
      });
    }
  }
);

// Multi-select move: the whole selection in ONE request. Per-document
// outcomes arrive together, so a name collision never costs a round trip
// of its own.
export const moveDocumentsToFolder = createAsyncThunk(
  "matterDetails/moveDocumentsBatch",
  async ({ documentIds, folderId, onConflict }, { rejectWithValue }) => {
    try {
      const response = await moveDocumentsBatchApi({
        documentIds,
        folderId,
        onConflict,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status,
        message:
          error?.response?.data?.detail ||
          "Couldn't move the files — try again.",
      });
    }
  }
);

export const fetchMatterDetailList = createAsyncThunk(
  "matterDetailslist/fetch",
  async ({ id, page, folder, sort_direction, sort_by }, { getState, rejectWithValue }) => {
    try {
      const { pageSize } = getState().matterDetails;
      const response = await fetchMatterDetailsList({ id, page, pageSize, sort_direction, sort_by, folder })
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchMatterDetailsFolder = createAsyncThunk(
  "matterDetailsFolder/fetch",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchMatterDetailFolders(id)
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Bookmarks API
export const fetchBookmarks = createAsyncThunk(
  "bookmarks/fetch",
  async (docId, { rejectWithValue }) => {
    try {
      const response = await fetchBookmarksAPI(docId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addBookmarks = createAsyncThunk(
  "bookmarks/add",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getBookmarksAPI(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const uploadMatterDetails = createAsyncThunk(
  "uploadmatterDetails/post",
  async ({ file, matter, id, folder, newly_created, relative_path, matterDetails }, { rejectWithValue }) => {
    try {
      // Use signed URL upload method internally when matterDetails is provided
      // This allows existing code to work without changes while using new signed URL flow
      if (matterDetails) {
        const { uploadSingleFileWithSignedUrl } = await import('../utils/uploadUtils');
        const response = await uploadSingleFileWithSignedUrl({
          file,
          matterDetails,
          uniqueId: folder ? `F${id}` : null,
          newly_created: true,
        });
        return response;
      } else {
        // Legacy fallback: use old uploadMatterDetail for backward compatibility
        // This handles cases where matterDetails is not provided
        const formData = new FormData();
        formData.append('files', file);
        formData.append('relative_path', file.webkitRelativePath);
        const response = await uploadMatterDetail({ 
          file: file, 
          matter: matter, 
          folder: (folder ? id : null), 
          newly_created, 
          relative_path 
        });
        return response;
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
)

export const createFolders = createAsyncThunk(
  "uploadfolder/post",
  async ({ name, matter_id, parent_id }, { rejectWithValue }) => {

    try {
      const response = await createFolder({ name, matter_id, parent_id });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
)

export const renameFolders = createAsyncThunk(
  "renameFolder/put",
  async ({ id, name }, { rejectWithValue }) => {

    try {
      const response = await renameFolder({ id: id, name: name })
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
)

export const fetchCategoryList = createAsyncThunk(
  "categoryList/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchCategoryLists()
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const fetchOpenedDocs = createAsyncThunk(
  "matterOpenDocs/fetch",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchOpenDocsList(id)
      return response;
    } catch (error) {
      console.error("Error in fetch open docs", error)
      return rejectWithValue("Unable to fetch open docs");
    }
  }
);

export const updateCategory = createAsyncThunk(
  "categoryUpdate/put",
  async ({ id, category }, { rejectWithValue }) => {

    try {
      const response = await categoryUpdate({ id: id, category: category })
      return response.data;
    } catch (error) {
      console.error("Error in update category", error)
      return rejectWithValue(error);
    }
  }
)
export const addOpenedDoc = createAsyncThunk(
  "matterOpenDocs/post",
  async ({ matterId, docId }, { rejectWithValue }) => {

    try {
      const response = await postOpenDoc(matterId, docId)
      return response;
    } catch (error) {
      console.error("Error in add opened docs", error)
      return rejectWithValue(error);
    }
  }
)

export const removeOpenedDoc = createAsyncThunk(
  "matterOpenDocs/delete",
  async ({ matterId, docId }, { rejectWithValue }) => {

    try {
      const response = await removeOpenDoc(matterId, docId)
      return response;
    } catch (error) {
      console.error("Error in remove open docs", error)
      return rejectWithValue(error);
    }
  }
)

export const deleteDoc = createAsyncThunk(
  "matterDocs/delete",
  async ({ docId }, { rejectWithValue }) => {

    try {
      const response = await deleteDocumentApi(docId)
      return response;
    } catch (error) {
      console.error("Error in delete docs", error)
      return rejectWithValue(error);
    }
  }
)

export const reviewAction = createAsyncThunk(
  "matterDocs/reviewAction",
  async (
    { shareId, reviewStatus, comment, nextReviewerId = null },
    { rejectWithValue }
  ) => {

    try {
      const response = await reviewActionApi(
        shareId,
        reviewStatus,
        comment,
        nextReviewerId
      )
      return response;
    } catch (error) {
      console.error("Error in review action", error)
      return rejectWithValue(error);
    }
  }
)

export const reviewQueueAction = createAsyncThunk(
  "matterDocs/reviewQueueAction",
  async ({ shareId, action, notes = "" }, { rejectWithValue }) => {
    try {
      const response = await reviewQueueActionApi(shareId, action, notes);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error updating review queue", error);
      return rejectWithValue(error);
    }
  }
);

export const deleteFolder = createAsyncThunk(
  "matterFolders/delete",
  async ({ folderId }, { rejectWithValue }) => {

    try {
      const response = await deleteFolderApi(folderId)
      return response;
    } catch (error) {
      console.error("Error in delete folder", error)
      return rejectWithValue(error);
    }
  }
)

export const getShareRecipients = createAsyncThunk(
  "matterDocs/getRecipients",
  async ({ docId, reviewersOnly }, { rejectWithValue }) => {
    try {
      const response = await getShareRecipientsApi(docId, reviewersOnly);
      return response;
    } catch (error) {
      console.error("Error in get share recipients API", error);
      return rejectWithValue(error);
    }
  }
);

export const shareDocument = createAsyncThunk(
  "matterDocs/shareDocument",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await shareDocumentApi(payload);
      if (response.status !== 201) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in sharing document", error);
      return rejectWithValue(error);
    }
  }
);

export const remindReview = createAsyncThunk(
  "matterDocs/remindReview",
  async (shareId, { rejectWithValue }) => {
    try {
      const response = await remindReviewApi(shareId);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const remindSignature = createAsyncThunk(
  "matterDocs/remindSignature",
  async (signatureId, { rejectWithValue }) => {
    try {
      const response = await remindSignatureApi(signatureId);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const cancelSignatureRequest = createAsyncThunk(
  "matterDocs/cancelSignatureRequest",
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await cancelSignatureRequestApi(requestId);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error cancelling signature request", error);
      return rejectWithValue(error);
    }
  }
);

export const sharingContext = createAsyncThunk(
  "matterDocs/shareContext",
  async (docId, { rejectWithValue }) => {
    try {
      const response = await getShareContext(docId);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const submitAction = createAsyncThunk(
  "matterDocs/submitAction",
  async ({ docId, comment }, { rejectWithValue }) => {

    try {
      const response = await submitActionApi(docId, comment)
      return response;
    } catch (error) {
      console.error("Error in submit action", error)
      return rejectWithValue(error);
    }
  }
)


export const fetchReviewNotes = createAsyncThunk(
  "matterDocs/fetchReviewNotes",
  async (docId, { rejectWithValue }) => {
    try {
      const response = await fetchReviewNotesApi(docId);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in fetching review notes", error);
      return rejectWithValue(error);
    }
  }
);

export const postHighlightedMatter = createAsyncThunk(
  "matterDocs/postHighlightedMatter",
  async ({ matterId, docId, docType, tabAction = null }, { rejectWithValue }) => {
    try {
      const response = await postMatterHighlight(matterId, docId, docType, tabAction);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in posting highlighted matter", error);
      return rejectWithValue(error);
    }
  }
);

export const postLastPageViewed = createAsyncThunk(
  "matterDocs/postLastPageViewed",
  async ({ docId, page_number }, { rejectWithValue }) => {
    try {
      const response = await postLastPageView(docId, page_number);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in posting highlighted matter", error);
      return rejectWithValue(error);
    }
  }
);


export const fetchTemplateFields = createAsyncThunk(
  "matterDocs/fetchTemplateFields",
  async (payload, { rejectWithValue }) => {
    try {
      // Handle both single ID (string/number) and object with templateId and matter_id
      const templateId = typeof payload === 'object' && payload !== null ? payload.templateId : payload;
      const matter_id = typeof payload === 'object' && payload !== null ? payload.matter_id : undefined;
      
      const response = await fetchTemplateFieldsApi(templateId, matter_id);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in fetching template fields", error);
      return rejectWithValue(error);
    }
  }
);

export const mergeTemplateFields = createAsyncThunk(
  "matterDocs/mergeTemplateFields",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await mergeTemplateFieldsApi(payload);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const previewTemplateFields = createAsyncThunk(
  "matterDocs/previewTemplateFields",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await previewTemplateFieldsApi(payload);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in preview template", error);
      return rejectWithValue(error);
    }
  }
);

export const uploadTemplate = createAsyncThunk(
  "matterDocs/uploadTemplate",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await uploadTemplateApi(payload);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const deleteHighlightMatter = createAsyncThunk(
  "mattersDocs/deleteHighlights",
  async (matterId, { rejectWithValue }) => {
    try {
      const response = await deleteMatterHighlight(matterId);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in deleting Highlight", error);
      return rejectWithValue(error);
    }
  }
);

export const sendForESign = createAsyncThunk(
  "matterDocs/sendForESign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await sendForESignApi(payload);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const initiateESign = createAsyncThunk(
  "matterDocs/initiateESign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await initiateESignApi(payload);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const fetchExternalShareDetails = createAsyncThunk(
  "externalShareDetails/fetch",
  async (document_id, { rejectWithValue }) => {
    try {
      const response = await fetchExternalShareDetail(document_id);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getGeneratedPassword = createAsyncThunk(
  "generatePassword/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await generatePassword();
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitExternalShare = createAsyncThunk(
  "submitExternalshare/update",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await externalShare(payload);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in External share", error);
      return rejectWithValue(error.response?.data);
    }
  }
);

export const revokeExternalShare = createAsyncThunk(
  "revokeExternalshare/update",
  async ({document_id}, { rejectWithValue }) => {
    try {
      const response = await revokeShare({document_id});
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const getDocumentDetails = createAsyncThunk(
  "getDocumentDetails/fetch",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getDocumentDetail(id);
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchPreviewPDFService = createAsyncThunk(
  "matterDocs/fetchPreviewPDF",
  async ({ docId, key }, { rejectWithValue }) => {
    try {
      const response = await fetchPreviewPDF(docId, key);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in fetching preview PDF", error);
      return rejectWithValue(error);
    }
  }
);

export const fetchBreadCrumbs = createAsyncThunk( 
  "matterBreadCrumb/fetchBreadCrumb",
  async ({ item_id }, { rejectWithValue }) => {
    try {
      const response = await fetchBreadCrumb(item_id);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in fetching BreadCrumb", error);
      return rejectWithValue(error);
    }
  });

export const updateTabOrder = createAsyncThunk(
  "matterTabs/updateTaborder",
  async ({ matter_id, document_id, new_position }, { rejectWithValue }) => {
    try {
      const response = await updateTabordering({ matter_id, document_id, new_position });
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in updating Tab order", error);
      return rejectWithValue(error);
    }
  }
)

export const chatBot = createAsyncThunk(
  "matterTabs/chatBot",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await chatBotApi(payload);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in chat bot", error);
      return rejectWithValue(error);
    }
  }
)

const MatterDetailsSlice = createSlice({
  name: "matter",
  initialState: {
    matterDetails: {},
    matterDetailsList: [],
    matterDetailsTree: {},
    selectedDetail: {},
    docListingPage: 1,
    totalPages: 0,
    totalRecords: 0,
    pageSize: 25,
    status: "idle", // idle, loading, succeeded, failed
    openDocsStatus: "idle", // idle, loading, succeeded, failed
    matterDetailsStatus: "idle", // idle, loading, succeeded, failed
    // True while a `fetchMatterDetail` is in-flight AFTER the initial
    // bootstrap already succeeded — i.e. an upload-driven background
    // refresh. Kept separate from `matterDetailsStatus` so the
    // `MatterDetailsShellSkeleton` overlay (gated on
    // matterDetailsStatus === "idle" || "loading" in
    // pages/MatterDetails/MatterDetails.js) does not flash on top of an
    // already-rendered directory while the user is uploading dozens or
    // hundreds of files. See uploadUtils.js::runPagination — that helper
    // re-dispatches `fetchMatterDetail` after every batch of 25
    // optimized files, which on a 562-file VBMS zip upload was producing
    // ~22 skeleton flashes mid-upload.
    isRefreshingMatterDetails: false,
    matterDetailsLIstStatus:"idle",
    upload: 0,
    error: null,
    loader: false,
    tabClicked: false,
    resetPagination: false,
    selectedKeys: [],
    expandedKeys: [],
    categoryList: [],
    openDocs: [],
    updateMeta:{},
    templates:[],
    templateFields:{},
    mergedDocument: {},
    eSign: {},
    externalShareDetails:{},
    documentDetail:{},
    deleteDocuId:null,
    chatBotResponse:{},
    bookmarks: {},
    addingBookmarks: false,
  },
  reducers: {
    updateCount(state,action) {
      state.upload = action.payload;
    },
    resetMatterDetails(state) {
      state.selectedDetail = {};
      state.matterDetails = {};
      state.matterDetailsList = [];
      state.matterDetailsTree = {};
    },
    setSelectedDetails(state, action) {
      state.selectedDetail = action.payload;
    },
    setSelectedKeys(state, action) {
      state.selectedKeys = action.payload;
    },
    setExpandedKeys(state, action) {
      state.expandedKeys = [...new Set(action.payload)];
    },
    setOpenDocs(state, action) {
      state.openDocs = action.payload;
    },
    setTabClick(state, action) {
      state.tabClicked = action.payload;
    },
    setResetPagination(state, action) {
      state.resetPagination = action.payload;
    },
    setDocListingPageSize(state, action) {
      state.pageSize = action.payload;
    },
    setDocListingPage(state, action) {
      state.docListingPage = action.payload;
    },
    setUpdateMeta(state, action) {
      state.updateMeta = action.payload;
    },
    setTemplateFields(state,action){
      state.templateFields = action.payload;
    },
    setMergedDocument(state,action){
      state.mergedDocument = action.payload;
    },
    resetStatuses: (state) => {
      state.openDocsStatus = "idle";
      state.matterDetailsStatus = "idle";
      state.isRefreshingMatterDetails = false;
    },
    resetMatterDetailList: (state) => {
      state.matterDetailsList = [];
    },
    updateDocumentDetail: (state, action) => {
      state.documentDetail = {
        ...state.documentDetail,
        ...action.payload,
      };
    },
    optimisticCategoryMove: (state, action) => {
      applyOptimisticCategoryMove(state, action.payload);
    },
    reconcileCategoryMove: (state, action) => {
      applyOptimisticCategoryMove(state, action.payload);
    },
    // Rename a document IN PLACE across the tree, the folder listing, and
    // the open document detail. Renames used to wait for the full matter
    // tree refetch to show the new name — seconds on big matters, which
    // read as "glitchy" (Matthew, 2026-07-22). The background refetch
    // still reconciles authoritatively afterwards.
    applyDocumentRename: (state, action) => {
      const { documentId, name } = action.payload || {};
      if (!documentId || !name) return;
      const idStr = String(documentId);
      const matches = (doc) =>
        doc &&
        (String(doc.id) === idStr ||
          String(doc.document_id ?? "") === idStr ||
          String(doc.unique_id ?? "") === idStr ||
          String(doc.unique_id ?? "") === `D${idStr}`);

      const renameInDocs = (docs) => {
        for (const doc of Array.isArray(docs) ? docs : []) {
          if (matches(doc)) doc.name = name;
        }
      };
      const renameInFolders = (folders) => {
        for (const folder of Array.isArray(folders) ? folders : []) {
          if (!folder || typeof folder !== "object") continue;
          renameInDocs(folder.documents);
          renameInFolders(folder.subfolders || folder.folders);
        }
      };
      renameInDocs(state.matterDetailsTree?.documents);
      renameInFolders(state.matterDetailsTree?.folders);
      renameInDocs(state.matterDetailsList);
      if (matches(state.documentDetail)) {
        state.documentDetail.name = name;
      }
    },
    // Rename a folder in the already-loaded tree/list immediately after the
    // API confirms success. Previously FolderContextMenu refetched the full
    // matter hierarchy and the visible name did not change until that large
    // response returned (five-second live repro on William Young (Kenny)).
    applyFolderRename: (state, action) => {
      const { folderId, name, timeUpdated } = action.payload || {};
      if (!folderId || !name) return;
      const idStr = String(folderId);
      const matches = (folder) => {
        if (!folder) return false;
        const uniqueId = String(folder.unique_id ?? "");
        return (
          String(folder.id ?? "") === idStr ||
          String(folder.folder_id ?? "") === idStr ||
          uniqueId === idStr ||
          uniqueId === `F${idStr}`
        );
      };
      const rename = (folder) => {
        if (!matches(folder)) return;
        folder.name = name;
        if (timeUpdated) {
          folder.time_updated = timeUpdated;
        }
      };
      const visit = (folders) => {
        for (const folder of Array.isArray(folders) ? folders : []) {
          if (!folder || typeof folder !== "object") continue;
          rename(folder);
          visit(folder.subfolders || folder.folders);
        }
      };

      visit(state.matterDetailsTree?.folders);
      for (const row of Array.isArray(state.matterDetailsList)
        ? state.matterDetailsList
        : []) {
        rename(row);
      }
      if (state.matterDetails?.folder === true) {
        rename(state.matterDetails);
      }
    },
    optimisticFolderMove: (state, action) => {
      applyOptimisticFolderMove(state, action.payload);
    },
    reconcileFolderMove: (state, action) => {
      applyOptimisticFolderMove(state, action.payload);
    },
    restoreCategoryMoveSnapshot: (state, action) => {
      const snapshot = action.payload || {};
      if (snapshot.matterDetailsTree !== undefined) {
        state.matterDetailsTree = snapshot.matterDetailsTree;
      }
      if (snapshot.matterDetailsList !== undefined) {
        state.matterDetailsList = snapshot.matterDetailsList;
      }
      if (snapshot.updateMeta !== undefined) {
        state.updateMeta = snapshot.updateMeta;
      }
      if (snapshot.documentDetail !== undefined) {
        state.documentDetail = snapshot.documentDetail;
      }
      if (snapshot.totalRecords !== undefined) {
        state.totalRecords = snapshot.totalRecords;
      }
    },
    clearDocumentDetail: (state) => {
      state.documentDetail = {};
    },
     setDeleteDoc(state, action) {
      state.deleteDocuId = action.payload;
    },
    setBookmarks(state, action) {
      state.bookmarks = action.payload;
    },
    clearBookmarks(state) {
      state.bookmarks = {};
      state.error = null;
    },
    updateDocumentLastPageViewed: (state, action) => {
      const { documentId, lastPageViewed } = action.payload;
      if (!documentId || !state.matterDetailsTree) return;
      
      // Helper function to recursively update document in tree
      const updateDocumentInTree = (tree) => {
        if (!tree) return false;
        
        // Handle array of folders
        if (Array.isArray(tree)) {
          for (const item of tree) {
            if (updateDocumentInTree(item)) return true;
          }
          return false;
        }
        
        // Check documents in current folder
        if (tree.documents && Array.isArray(tree.documents)) {
          const docIndex = tree.documents.findIndex(
            doc => doc?.unique_id === `D${documentId}` || String(doc?.id) === String(documentId)
          );
          if (docIndex !== -1) {
            tree.documents[docIndex] = {
              ...tree.documents[docIndex],
              last_page_viewed: String(lastPageViewed)
            };
            return true;
          }
        }
        
        // Recursively check subfolders
        const subfolders = tree.folders || tree.subfolders || [];
        if (Array.isArray(subfolders) && subfolders.length > 0) {
          for (const subfolder of subfolders) {
            if (updateDocumentInTree(subfolder)) return true;
          }
        }
        
        return false;
      };
      
      // Update the document in matterDetailsTree
      updateDocumentInTree(state.matterDetailsTree);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatterDetail.pending, (state) => {
        state.status = "loading";
        // Only flip the directory-page skeleton gate if we have never
        // successfully loaded the matter yet (bootstrap or recovery
        // from a prior failure). Once `matterDetailsStatus === "succeeded"`,
        // any subsequent `fetchMatterDetail` is a background refresh
        // (typically driven by uploadUtils.js::runPagination after a
        // batch of file completions) and must not blank out the
        // directory the user is currently looking at. Tracked instead
        // by `isRefreshingMatterDetails` for any UI that wants to show a
        // subtle "refreshing" affordance.
        if (state.matterDetailsStatus === "succeeded") {
          state.isRefreshingMatterDetails = true;
        } else {
          state.matterDetailsStatus = "loading";
          state.isRefreshingMatterDetails = false;
        }
      })
      .addCase(fetchMatterDetail.fulfilled, (state, action) => {
        if (action.payload?.stale) {
          state.isRefreshingMatterDetails = false;
          return;
        }
        state.status = "succeeded";
        state.matterDetailsStatus = "succeeded";
        state.isRefreshingMatterDetails = false;
        state.matterDetails = { ...action.payload.data, folder: false };
        state.matterDetailsTree = action.payload.data
      })
      .addCase(fetchMatterDetail.rejected, (state, action) => {
        state.status = "failed";
        // Don't downgrade an already-loaded directory to "failed" just
        // because a background upload-driven refresh failed — the user's
        // current tree is still valid. Only flip to "failed" when we
        // never had a successful load to fall back on.
        if (state.matterDetailsStatus !== "succeeded") {
          state.matterDetailsStatus = "failed";
        }
        state.isRefreshingMatterDetails = false;
        state.error = action.error.message;
      })
      .addCase(uploadMatterDetails.pending, (state) => {
        state.status = "loading";
      })
      .addCase(uploadMatterDetails.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.upload += 1;
      })
      .addCase(uploadMatterDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchMatterDetailsFolder.pending, (state) => {
        state.status = "loading";
        state.loader = true;
      })
      .addCase(fetchMatterDetailsFolder.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loader = false;
        const folderData = action.payload.data || {};
        // The folder payload does not repeat the matter's display name, but
        // the upload tray needs it to say which client a file went to. Carry
        // it over from the matter we already loaded — only when that loaded
        // matter is the one this folder belongs to, so a stale matter can
        // never label another client's upload.
        const tree = state.matterDetailsTree;
        // Folder payloads name their matter in `matter` (id) and
        // `sf_matter_id`; the loaded matter carries the same two fields.
        const sameId = (a, b) => a != null && b != null && String(a) === String(b);
        const sameMatter =
          !!tree &&
          (sameId(folderData.matter, tree.id) ||
            sameId(folderData.sf_matter_id, tree.sf_matter_id));
        const carriedLabels = sameMatter
          ? {
              matter_displayName: folderData.matter_displayName ?? tree.matter_displayName,
              display_name: folderData.display_name ?? tree.display_name,
            }
          : {};
        state.matterDetails = { ...carriedLabels, ...folderData, folder: true };
      })
      .addCase(fetchMatterDetailsFolder.rejected, (state, action) => {
        state.status = "failed";
        state.loader = false;
        state.error = action.error.message;
      })
      .addCase(createFolders.pending, (state) => {
        state.status = "loading";
        state.loader = true;
      })
      .addCase(createFolders.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loader = false;
      })
      .addCase(createFolders.rejected, (state, action) => {
        state.status = "failed";
        state.loader = false;
      })
      .addCase(deleteDoc.pending, (state) => {
        state.status = "loading";
        state.loader = true;
      })
      .addCase(deleteDoc.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loader = false;
      })
      .addCase(deleteDoc.rejected, (state, action) => {
        state.status = "failed";
        state.loader = false;
      })
      .addCase(deleteFolder.pending, (state) => {
        state.status = "loading";
        state.loader = true;
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loader = false;
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.status = "failed";
        state.loader = false;
      })
      .addCase(fetchMatterDetailList.pending, (state) => {
        state.status = "loading";
         state.matterDetailsLIstStatus = "loading";
      })
      .addCase(fetchMatterDetailList.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.matterDetailsList = action.payload.data.results;
        state.totalPages = ((action.payload.data.count % 10) > 0) ? ((action.payload.data.count / 10) + 1) : (action.payload.data.count / 10);
        state.totalRecords = action.payload.data.count;
        state.matterDetailsLIstStatus = "succeeded";
      })
      .addCase(fetchMatterDetailList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
        state.matterDetailsLIstStatus = "failed";
      })
      .addCase(fetchCategoryList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCategoryList.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.categoryList = action.payload.data
      })
      .addCase(fetchCategoryList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchOpenedDocs.pending, (state) => {
        state.openDocsStatus = "loading";
      })
      .addCase(fetchOpenedDocs.fulfilled, (state, action) => {
        state.openDocsStatus = "succeeded";
        state.openDocs = deduplicateAndSortOpenDocs(action.payload.data);
      })
      .addCase(fetchOpenedDocs.rejected, (state, action) => {
        state.openDocsStatus = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchBookmarks.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBookmarks.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload && action.payload.data) {
          state.bookmarks = action.payload.data;
        }
      })
      .addCase(fetchBookmarks.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })
      .addCase(addBookmarks.pending, (state) => {
        state.addingBookmarks = true;
        state.error = null;
      })
      .addCase(addBookmarks.fulfilled, (state, action) => {
        state.addingBookmarks = false;
        if (action.payload && action.payload.data) {
          state.bookmarks = action.payload.data;
        }
      })
      .addCase(addBookmarks.rejected, (state, action) => {
        state.addingBookmarks = false;
        state.error = action.payload || action.error?.message;
      })
      .addCase(addOpenedDoc.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addOpenedDoc.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Add the new doc and then deduplicate and sort
        const updatedDocs = [...state.openDocs, action.payload.data];
        state.openDocs = deduplicateAndSortOpenDocs(updatedDocs);
      })
      .addCase(addOpenedDoc.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(removeOpenedDoc.pending, (state) => {
        state.status = "loading";
      })
      .addCase(removeOpenedDoc.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.openDocs = deduplicateAndSortOpenDocs(action.payload.data.remaining_tabs);
      })
      .addCase(removeOpenedDoc.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(updateCategory.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.updateMeta = action.payload;
      })
      .addCase(updateCategory.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchTemplateFields.fulfilled, (state, action) => {
        state.templateFields = action.payload.data
      })
      .addCase(mergeTemplateFields.fulfilled, (state, action) => {
        state.mergedDocument = action.payload.data
      })
      .addCase(sendForESign.fulfilled, (state, action) => {
        state.eSign = action.payload.data
      })
      .addCase(fetchExternalShareDetails.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchExternalShareDetails.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.externalShareDetails = action.payload.data;
      })
      .addCase(fetchExternalShareDetails.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getDocumentDetails.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.updateMeta = action.payload.data;
        state.documentDetail = action.payload.data;
      })
      .addCase(chatBot.fulfilled, (state, action) => {
        state.chatBotResponse = action.payload.data;
      })
  },
});
export const {
  updateCount,
  setSelectedDetails,
  resetMatterDetails,
  setExpandedKeys,
  setSelectedKeys,
  setOpenDocs,
  setTabClick,
  setResetPagination,
  setDocListingPage,
  setUpdateMeta,
  setMergedDocument,
  setTemplateFields,
  resetStatuses,
  resetMatterDetailList,
  updateDocumentDetail,
  optimisticCategoryMove,
  reconcileCategoryMove,
  applyDocumentRename,
  applyFolderRename,
  optimisticFolderMove,
  reconcileFolderMove,
  restoreCategoryMoveSnapshot,
  clearDocumentDetail,
  updateDocumentLastPageViewed,
  setDocListingPageSize,
  setDeleteDoc,
  setBookmarks,
  clearBookmarks,
  setChatBotResponse
} = MatterDetailsSlice.actions;
export default MatterDetailsSlice.reducer;
